import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, createTextVNode as _createTextVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, createElementBlock as _createElementBlock, resolveDynamicComponent as _resolveDynamicComponent, toHandlers as _toHandlers, mergeProps as _mergeProps, vShow as _vShow, withDirectives as _withDirectives, renderSlot as _renderSlot, unref as _unref, normalizeStyle as _normalizeStyle, normalizeClass as _normalizeClass } from "vue"

const _hoisted_1 = { class: "text-h6" }
const _hoisted_2 = {
  key: 0,
  class: "d-flex"
}

import { shallowRef, computed, ref, watch, markRaw, provide } from 'vue';
    import { useI18n } from 'vue-i18n';
    import { useStore } from 'vuex';
    import { debounce } from 'lodash';

    import { COMPONENT } from '@/aquassist-shared/constants/types/node';
    import { NodeTree } from '@/interfaces/site-tree.types';
    import { Tab } from '@/store/modules/modalTab';
    import LockService from '@/utilities/lockService';
    import type { ComponentWithGetData } from '@/interfaces/common';
    import { ModalSize } from '@/interfaces/components/AQModal.types';

    
export default /*@__PURE__*/_defineComponent({
  __name: 'AQModal',
  props: {
    component: {},
    title: {},
    isConfirmDisabled: { type: Boolean, default: false },
    isSaveBtnVisible: { type: Boolean, default: false },
    dialog: { type: Boolean, default: true },
    modelValue: { type: Boolean, default: false },
    showNextPrevious: { type: Boolean, default: true },
    showLangs: { type: Boolean, default: false },
    lang: {},
    showConfirmButton: { type: Boolean, default: true },
    props: {},
    payload: {},
    modalSize: {},
    secondButtonLabel: { default: '' },
    skipValidation: { type: Boolean },
    componentRef: {},
    noFooter: { type: Boolean, default: false },
    isConfirmModal: { type: Boolean, default: false },
    listeners: {},
    modalId: { default: 0 },
    closeImmediatelyOnConfirm: { type: Boolean, default: true },
    onConfirm: {}
  },
  emits: ["confirm", "secondButton", "close", "update:modelValue", "update:lang"],
  setup(__props: any, { expose: __expose, emit: __emit }) {

    const { t } = useI18n();

    // Type guard to check if component has getData method
    function hasGetData(
        component: ComponentWithGetData,
    ): component is ComponentWithGetData & { getData: () => unknown } {
        return typeof (component as { getData?: () => unknown }).getData === 'function';
    }

    const props = __props;
    const emit = __emit;

    const requestPayload = ref<unknown>();
    const isConfirming = ref(false);

    const innerFormValidity = ref<null | boolean>(null);
    // Store validity per tab index
    const tabValidityMap = ref<Map<number, boolean>>(new Map());

    // Define the method you want to expose
    function onFormValidityChange(isValid: boolean, tabIndex?: number) {
        innerFormValidity.value = isValid;
        // Store validity for specific tab, active tab will be 0 if no tabIndex is provided
        // as all components load at the same time
        if (getTabs.value.length > 0) {
            const targetIndex = tabIndex !== undefined ? tabIndex : activeTabIndex.value;
            tabValidityMap.value.set(targetIndex, isValid);
        }
    }
    function setRequestPayload(data: unknown) {
        requestPayload.value = data;
    }

    // Provide the method to descendants
    provide('modalMethods', {
        onFormValidityChange,
        setRequestPayload,
    });

    const dialog = shallowRef(props.dialog || props.modelValue || false);
    // to fix when lang store is not available
    const displayedLangs = computed(() => {
        return props.showLangs ? ['en', 'fr'] : [];
    });

    // Store
    const store = useStore();
    const { dispatch } = store;

    // Getters
    const getTabs = computed<Tab[]>(() => store.getters['modalTab/getTabs'](props.modalId));
    const activeTabIndex = computed<number>({
        get: () => store.getters['modalTab/activeTab'](props.modalId),
        set: (val: number) => {
            dispatch('modalTab/changeActiveTab', { tabIndex: val, modalId: props.modalId });
        },
    });

    // computed
    const displayPrevious = computed(() => activeTabIndex.value > 0);
    const displayNext = computed(() => getTabs.value.length - 1 > activeTabIndex.value);
    const modalSize = computed(() => {
        switch (props.modalSize) {
            case 'small':
                return 500;
            case 'large':
                return 2200;
            case 'fullScreen':
                return 'fullScreen';
            default:
                return 1100; //medium
        }
    });

    // Vérifie si le formulaire interne est invalide
    const isFormInvalid = computed(() => {
        if (props.skipValidation) return false;
        if (props.isConfirmDisabled) return true;
        // If there are tabs, check validity
        if (getTabs.value.length > 0) {
            // For Next button: only check current tab
            if (displayNext.value) {
                const currentTabValidity = tabValidityMap.value.get(activeTabIndex.value);
                if (currentTabValidity !== undefined) {
                    return !currentTabValidity;
                }
                if (innerFormValidity.value !== null) {
                    return !innerFormValidity.value;
                }
                return true; // Disable until we know validity
            }

            // For Confirm button (last tab): check ALL visible tabs that have reported validity
            // This ensures all previous visible forms are valid before allowing confirmation
            for (const [tabIndex, isValid] of tabValidityMap.value.entries()) {
                const tab = getTabs.value[tabIndex];
                if (tab?.disabled) continue;
                if (!isValid) {
                    return true; // Disable if any visible tab is invalid
                }
            }

            // Also check current tab validity
            const currentTabValidity = tabValidityMap.value.get(activeTabIndex.value);
            if (currentTabValidity !== undefined) {
                return !currentTabValidity;
            }
            if (innerFormValidity.value !== null) {
                return !innerFormValidity.value;
            }
            return true; // Disable until we know validity
        }

        // Non-tab modal: respect child validity if provided
        if (innerFormValidity.value !== null) return !innerFormValidity.value;

        return props.isConfirmDisabled;
    });

    // Watch
    watch(
        () => props.dialog,
        (newVal) => {
            if (newVal !== undefined) {
                dialog.value = newVal;
            }
        },
        { immediate: true },
    );

    watch(
        () => props.modelValue,
        (newVal) => {
            if (newVal !== undefined) {
                dialog.value = newVal;
            }
        },
        { immediate: true },
    );

    watch(dialog, (newVal) => {
        emit('update:modelValue', newVal);
        if (!newVal) {
            isConfirming.value = false;
        }
    });
    const innerComponent = ref<ComponentWithGetData | null>(null);
    const tabComponentRefs = ref<Array<{ getData?: () => unknown } | null>>([]);

    const setTabComponentRef = (index: number, el: unknown) => {
        if (el === null) {
            // Component is being unmounted, safely set to null
            tabComponentRefs.value[index] = null;
        } else {
            tabComponentRefs.value[index] = el as { getData?: () => unknown } | null;
        }
    };

    __expose({
        getInnerComponent: () => innerComponent.value,
    });

    const releasLock = () => {
        const { actionName, node, lockAcquired } = (props.payload || {}) as unknown as {
            actionName?: string;
            lockAcquired?: boolean;
            node?: NodeTree;
        };
        if (lockAcquired && actionName && node && node.nodeType === COMPONENT) {
            LockService.releaseLock(node.ref || node.id, actionName);
        }
    };

    // handlers
    function handleClose() {
        emit('close');
        dialog.value = false;
        // Clear validity map when closing modal
        tabValidityMap.value.clear();
        innerFormValidity.value = null;
        releasLock();
    }

    function handleChildSubmitSuccess(data: unknown) {
        emit('confirm', data);
        dialog.value = false;
        // Clear validity map when closing modal
        tabValidityMap.value.clear();
        innerFormValidity.value = null;
    }

    async function handleSave() {
        if (isConfirming.value) return;
        isConfirming.value = true;
        try {
            let data: unknown = null;
            // If tabs exist, collect data from all tab components - just for multi step forms
            if (getTabs.value.length > 0) {
                const allData: Record<string, unknown> = {};
                tabComponentRefs.value.forEach((tabRef: { getData?: () => unknown } | null) => {
                    if (tabRef && typeof tabRef.getData === 'function') {
                        const tabData = tabRef.getData();
                        Object.assign(allData, tabData);
                    }
                });
                data = allData;
            } else {
                //  single tab form
                const component = innerComponent.value;
                if (component && hasGetData(component)) {
                    // await so forms whose getData is async (e.g. graph export) resolve;
                    // a synchronous getData passes through unchanged.
                    data = await component.getData();
                } else if (requestPayload.value) {
                    data = requestPayload.value;
                }
            }
            if (props.onConfirm) {
                await props.onConfirm(data);
            } else {
                emit('confirm', data);
            }
            if (props.closeImmediatelyOnConfirm) {
                dialog.value = false;
            }
        } catch (error) {
            console.error('Error in handleSave:', error);
            isConfirming.value = false;
            if (props.closeImmediatelyOnConfirm) {
                dialog.value = false;
            }
        } finally {
            releasLock();
        }
        // Clear validity map when closing modal
        tabValidityMap.value.clear();
        innerFormValidity.value = null;
    }

    const debouncedHandleSave = debounce(handleSave, 500, {
        leading: true,
        trailing: false,
    });

    function handleSecondButton() {
        // If tabs exist, collect data from all tab components - just for multi step forms
        if (getTabs.value.length > 0) {
            const allData: Record<string, unknown> = {};
            tabComponentRefs.value.forEach((tabRef: { getData?: () => unknown } | null) => {
                if (tabRef && typeof tabRef.getData === 'function') {
                    const data = tabRef.getData();
                    Object.assign(allData, data);
                }
            });
            emit('secondButton', allData);
        } else {
            const component = innerComponent.value;

            if (component && hasGetData(component)) {
                const data = component.getData();
                emit('secondButton', data);
            } else {
                emit('secondButton', null);
            }
        }
        dialog.value = false;

        dispatch('modalTab/clearTabs', props.modalId);
    }

    function updateLang(lang: string) {
        emit('update:lang', lang);
    }

    async function previous() {
        // Reset validity when changing tabs so new tab can report its state
        innerFormValidity.value = null;
        const prevIndex = store.getters['modalTab/previousVisibleTab'](
            props.modalId,
            activeTabIndex.value,
        );
        await dispatch('modalTab/changeActiveTab', {
            tabIndex: prevIndex,
            modalId: props.modalId,
        });
    }

    async function next() {
        // Reset validity when changing tabs so new tab can report its state
        innerFormValidity.value = null;
        const nextIndex = store.getters['modalTab/nextVisibleTab'](
            props.modalId,
            activeTabIndex.value,
        );
        await dispatch('modalTab/changeActiveTab', {
            tabIndex: nextIndex,
            modalId: props.modalId,
        });
    }

    const lang = ref(props.lang || 'en');

return (_ctx: any,_cache: any) => {
  const _component_v_col = _resolveComponent("v-col")!
  const _component_AQButton = _resolveComponent("AQButton")!
  const _component_v_row = _resolveComponent("v-row")!
  const _component_AQSelect = _resolveComponent("AQSelect")!
  const _component_v_card_title = _resolveComponent("v-card-title")!
  const _component_v_divider = _resolveComponent("v-divider")!
  const _component_v_tab = _resolveComponent("v-tab")!
  const _component_v_tabs = _resolveComponent("v-tabs")!
  const _component_v_card_text = _resolveComponent("v-card-text")!
  const _component_v_sheet = _resolveComponent("v-sheet")!
  const _component_v_card_actions = _resolveComponent("v-card-actions")!
  const _component_v_card = _resolveComponent("v-card")!
  const _component_v_dialog = _resolveComponent("v-dialog")!

  return (_openBlock(), _createBlock(_component_v_dialog, {
    modelValue: dialog.value,
    "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((dialog).value = $event)),
    rounded: "",
    width: modalSize.value,
    persistent: ""
  }, {
    default: _withCtx(() => [
      _createVNode(_component_v_card, {
        class: "d-flex flex-column aq-modal",
        style: {"height":"80vh"}
      }, {
        default: _withCtx(() => [
          _createVNode(_component_v_card_title, { class: "d-flex align-start pa-4 aq-modal-header" }, {
            default: _withCtx(() => [
              _createVNode(_component_v_row, { class: "align-center w-100" }, {
                default: _withCtx(() => [
                  _createVNode(_component_v_col, { class: "d-flex align-center" }, {
                    default: _withCtx(() => [
                      _createElementVNode("span", _hoisted_1, _toDisplayString(__props.title), 1)
                    ]),
                    _: 1
                  }),
                  _createVNode(_component_v_col, { class: "d-flex justify-end" }, {
                    default: _withCtx(() => [
                      _createVNode(_component_AQButton, {
                        variant: "outlined",
                        prependIcon: "mdi-close",
                        size: "small",
                        onClick: handleClose
                      }, {
                        default: _withCtx(() => [
                          _createTextVNode(_toDisplayString(_ctx.$t('forms.labels.close')), 1)
                        ]),
                        _: 1
                      })
                    ]),
                    _: 1
                  })
                ]),
                _: 1
              }),
              _createVNode(_component_v_row, { class: "align-center" }, {
                default: _withCtx(() => [
                  (__props.showLangs)
                    ? (_openBlock(), _createBlock(_component_v_col, {
                        key: 0,
                        class: "d-flex align-center mt-2"
                      }, {
                        default: _withCtx(() => [
                          _createVNode(_component_AQSelect, {
                            modelValue: lang.value,
                            "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((lang).value = $event)),
                            label: _ctx.$t('forms.labels.lang'),
                            items: displayedLangs.value,
                            outlined: "",
                            "hide-details": "",
                            dense: "",
                            rounded: "",
                            style: {"width":"200px"},
                            onInput: updateLang
                          }, null, 8, ["modelValue", "label", "items"])
                        ]),
                        _: 1
                      }))
                    : _createCommentVNode("", true)
                ]),
                _: 1
              })
            ]),
            _: 1
          }),
          _createVNode(_component_v_divider),
          (getTabs.value.length)
            ? (_openBlock(), _createBlock(_component_v_tabs, {
                key: 0,
                modelValue: activeTabIndex.value,
                "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((activeTabIndex).value = $event)),
                class: "aq-modal-tabs"
              }, {
                default: _withCtx(() => [
                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(getTabs.value, (tab, index) => {
                    return (_openBlock(), _createBlock(_component_v_tab, {
                      key: index,
                      disabled: tab.disabled
                    }, {
                      default: _withCtx(() => [
                        _createTextVNode(_toDisplayString(tab.name), 1)
                      ]),
                      _: 2
                    }, 1032, ["disabled"]))
                  }), 128))
                ]),
                _: 1
              }, 8, ["modelValue"]))
            : _createCommentVNode("", true),
          (getTabs.value.length)
            ? (_openBlock(), _createBlock(_component_v_divider, { key: 1 }))
            : _createCommentVNode("", true),
          _createVNode(_component_v_sheet, {
            class: "flex-grow-1",
            style: {"overflow-y":"auto","min-height":"0"}
          }, {
            default: _withCtx(() => [
              _createVNode(_component_v_card_text, { class: "pa-4" }, {
                default: _withCtx(() => [
                  (getTabs.value.length)
                    ? (_openBlock(true), _createElementBlock(_Fragment, { key: 0 }, _renderList(getTabs.value, (tab, index) => {
                        return _withDirectives((_openBlock(), _createElementBlock("div", { key: index }, [
                          (_openBlock(), _createBlock(_resolveDynamicComponent(tab.component && markRaw(tab.component)), _mergeProps({
                            ref_for: true,
                            ref: (el) => setTabComponentRef(index, el)
                          }, { ref_for: true }, {
                                    ..._ctx.$attrs,
                                    ...(props.payload || {}),
                                    ...(tab?.props || {}),
                                    modalTabIndex: index,
                                }, _toHandlers({ ..._ctx.$attrs, ...(props.listeners || {}) }), { onSubmitSuccess: handleChildSubmitSuccess }), null, 16))
                        ])), [
                          [_vShow, index === activeTabIndex.value]
                        ])
                      }), 128))
                    : (props.component)
                      ? (_openBlock(), _createBlock(_resolveDynamicComponent(props.component && markRaw(props.component)), _mergeProps({
                          key: 1,
                          ref_key: "innerComponent",
                          ref: innerComponent
                        }, {
                            ..._ctx.$attrs,
                            ...(props.payload || {}),
                        }, _toHandlers({ ..._ctx.$attrs, ...(props.listeners || {}) }), { onSubmitSuccess: handleChildSubmitSuccess }), null, 16))
                      : _createCommentVNode("", true),
                  (!(props.component || getTabs.value.length))
                    ? _renderSlot(_ctx.$slots, "default", { key: 2 })
                    : _createCommentVNode("", true),
                  (__props.isConfirmModal)
                    ? _renderSlot(_ctx.$slots, "body", { key: 3 })
                    : _createCommentVNode("", true)
                ]),
                _: 3
              })
            ]),
            _: 3
          }),
          _createVNode(_component_v_divider),
          (__props.showConfirmButton && !__props.noFooter)
            ? (_openBlock(), _createBlock(_component_v_card_actions, {
                key: 2,
                class: "pa-4 justify-end aq-modal-footer"
              }, {
                default: _withCtx(() => [
                  _createVNode(_component_v_row, { class: "align-center w-100" }, {
                    default: _withCtx(() => [
                      _createVNode(_component_v_col, { class: "d-flex align-center" }, {
                        default: _withCtx(() => [
                          _createVNode(_component_v_card_actions, null, {
                            default: _withCtx(() => [
                              (getTabs.value.length && displayPrevious.value)
                                ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
                                    _createVNode(_component_AQButton, {
                                      variant: "outlined",
                                      prependIcon: "mdi-arrow-left",
                                      size: "small",
                                      onClick: previous
                                    }, {
                                      default: _withCtx(() => [
                                        _createTextVNode(_toDisplayString(_ctx.$t('pagination.previous')), 1)
                                      ]),
                                      _: 1
                                    })
                                  ]))
                                : _createCommentVNode("", true)
                            ]),
                            _: 1
                          })
                        ]),
                        _: 1
                      }),
                      (__props.secondButtonLabel)
                        ? (_openBlock(), _createBlock(_component_v_col, {
                            key: 0,
                            class: "d-flex justify-end",
                            cols: "auto"
                          }, {
                            default: _withCtx(() => [
                              _createVNode(_component_AQButton, {
                                variant: "outlined",
                                style: _normalizeStyle({
                                backgroundColor:
                                    __props.secondButtonLabel === _unref(t)('forms.labels.delete')
                                        ? 'rgb(var(--v-theme-aquassistRed)) !important'
                                        : '',
                            }),
                                color: "secondary",
                                onClick: handleSecondButton
                              }, {
                                default: _withCtx(() => [
                                  _createTextVNode(_toDisplayString(__props.secondButtonLabel), 1)
                                ]),
                                _: 1
                              }, 8, ["style"])
                            ]),
                            _: 1
                          }))
                        : _createCommentVNode("", true),
                      (__props.showConfirmButton)
                        ? (_openBlock(), _createBlock(_component_v_col, {
                            key: 1,
                            class: "d-flex justify-end",
                            cols: "auto"
                          }, {
                            default: _withCtx(() => [
                              _createVNode(_component_AQButton, {
                                variant: "flat",
                                color: "secondary",
                                class: _normalizeClass(!displayNext.value ? 'confirm-modal-btn' : ''),
                                disabled: isFormInvalid.value || isConfirming.value,
                                loading: isConfirming.value,
                                onClick: _cache[2] || (_cache[2] = ($event: any) => (displayNext.value ? next() : _unref(debouncedHandleSave)()))
                              }, {
                                default: _withCtx(() => [
                                  _createTextVNode(_toDisplayString(displayNext.value
                                    ? _unref(t)('forms.btn.next')
                                    : __props.isSaveBtnVisible
                                    ? _unref(t)('forms.btn.save')
                                    : _unref(t)('forms.btn.confirm')), 1)
                                ]),
                                _: 1
                              }, 8, ["class", "disabled", "loading"])
                            ]),
                            _: 1
                          }))
                        : _createCommentVNode("", true)
                    ]),
                    _: 1
                  })
                ]),
                _: 1
              }))
            : (_openBlock(), _createBlock(_component_v_card_actions, {
                key: 3,
                class: "noFooter-v-card"
              }))
        ]),
        _: 3
      })
    ]),
    _: 3
  }, 8, ["modelValue", "width"]))
}
}

})